import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useShipping } from './ShippingContext';

const EcommerceContext = createContext();

export const useEcommerce = () => {
  const context = useContext(EcommerceContext);
  if (!context) {
    throw new Error('useEcommerce must be used within an EcommerceProvider');
  }
  return context;
};

export const EcommerceProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [syncStatus, setSyncStatus] = useState({});
  const { user } = useAuth();
  const { createShipment } = useShipping();

  // Available e-commerce platforms
  const availablePlatforms = [
    {
      id: 'shopify',
      name: 'Shopify',
      logo: '🛍️',
      description: 'Integración con tiendas Shopify',
      fields: [
        { name: 'store_url', label: 'URL de la tienda', type: 'url', required: true },
        { name: 'access_token', label: 'Token de acceso', type: 'password', required: true },
        { name: 'webhook_secret', label: 'Webhook secret', type: 'password', required: false }
      ],
      features: ['Sincronización automática', 'Webhooks', 'Productos', 'Órdenes', 'Inventario']
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      logo: '🛒',
      description: 'Integración con tiendas WooCommerce',
      fields: [
        { name: 'store_url', label: 'URL de la tienda', type: 'url', required: true },
        { name: 'consumer_key', label: 'Consumer Key', type: 'password', required: true },
        { name: 'consumer_secret', label: 'Consumer Secret', type: 'password', required: true }
      ],
      features: ['API REST', 'Órdenes', 'Productos', 'Clientes', 'Inventario']
    },
    {
      id: 'tienda_nube',
      name: 'Tienda Nube',
      logo: '☁️',
      description: 'Integración con tiendas Nube',
      fields: [
        { name: 'store_id', label: 'ID de la tienda', type: 'text', required: true },
        { name: 'access_token', label: 'Token de acceso', type: 'password', required: true },
        { name: 'app_id', label: 'App ID', type: 'text', required: true }
      ],
      features: ['Sincronización', 'Órdenes', 'Productos', 'Webhooks']
    },
    {
      id: 'prestashop',
      name: 'PrestaShop',
      logo: '🏪',
      description: 'Integración con tiendas PrestaShop',
      fields: [
        { name: 'store_url', label: 'URL de la tienda', type: 'url', required: true },
        { name: 'webservice_key', label: 'Webservice Key', type: 'password', required: true }
      ],
      features: ['Webservice API', 'Órdenes', 'Productos', 'Clientes']
    },
    {
      id: 'magento',
      name: 'Magento',
      logo: '🔶',
      description: 'Integración con tiendas Magento',
      fields: [
        { name: 'store_url', label: 'URL de la tienda', type: 'url', required: true },
        { name: 'username', label: 'Username', type: 'text', required: true },
        { name: 'password', label: 'Password', type: 'password', required: true }
      ],
      features: ['REST API', 'Órdenes', 'Productos', 'Clientes', 'Inventario']
    }
  ];

  useEffect(() => {
    if (user) {
      loadUserIntegrations();
    }
  }, [user]);

  const loadUserIntegrations = async () => {
    try {
      setLoading(true);
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock integrations
      const mockIntegrations = [
        {
          id: 'INT-001',
          platform: 'shopify',
          name: 'Mi Tienda Shopify',
          status: 'connected',
          createdAt: new Date('2025-01-10').toISOString(),
          lastSync: new Date('2025-01-16').toISOString(),
          ordersCount: 145,
          config: {
            store_url: 'https://mi-tienda.myshopify.com',
            access_token: 'shpat_xxxxx',
            webhook_secret: 'xxxxx'
          }
        },
        {
          id: 'INT-002',
          platform: 'woocommerce',
          name: 'Mi Tienda WooCommerce',
          status: 'error',
          createdAt: new Date('2025-01-05').toISOString(),
          lastSync: new Date('2025-01-15').toISOString(),
          ordersCount: 78,
          config: {
            store_url: 'https://mi-tienda.com',
            consumer_key: 'ck_xxxxx',
            consumer_secret: 'cs_xxxxx'
          }
        }
      ];
      
      setIntegrations(mockIntegrations);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async (platform, config) => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate configuration
      const platformConfig = availablePlatforms.find(p => p.id === platform);
      if (!platformConfig) {
        throw new Error('Plataforma no soportada');
      }
      
      // Validate required fields
      for (const field of platformConfig.fields) {
        if (field.required && !config[field.name]) {
          throw new Error(`${field.label} es requerido`);
        }
      }
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Test connection
      const testResult = await testConnection(platform, config);
      if (!testResult.success) {
        throw new Error(testResult.error);
      }
      
      const newIntegration = {
        id: `INT-${Date.now()}`,
        platform,
        name: config.name || `${platformConfig.name} Integration`,
        status: 'connected',
        createdAt: new Date().toISOString(),
        lastSync: new Date().toISOString(),
        ordersCount: 0,
        config
      };
      
      setIntegrations(prev => [...prev, newIntegration]);
      return { success: true, integration: newIntegration };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateIntegration = async (integrationId, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIntegrations(prev => prev.map(integration => 
        integration.id === integrationId 
          ? { ...integration, ...updates, lastSync: new Date().toISOString() }
          : integration
      ));
      
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteIntegration = async (integrationId) => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIntegrations(prev => prev.filter(integration => integration.id !== integrationId));
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (platform, config) => {
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock test connection
      const success = Math.random() > 0.2; // 80% success rate
      
      if (success) {
        return { success: true, message: 'Conexión exitosa' };
      } else {
        return { success: false, error: 'Error de conexión. Verifica las credenciales.' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const syncOrders = async (integrationId) => {
    try {
      setLoading(true);
      setSyncStatus(prev => ({ ...prev, [integrationId]: 'syncing' }));
      
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Mock sync orders
      const mockOrders = Array.from({ length: 5 }, (_, i) => ({
        id: `ORD-${Date.now()}-${i}`,
        integrationId,
        platform: integrations.find(int => int.id === integrationId)?.platform,
        orderNumber: `#${1000 + i}`,
        customerName: `Cliente ${i + 1}`,
        customerEmail: `cliente${i + 1}@example.com`,
        shippingAddress: {
          address: `Dirección ${i + 1}`,
          city: 'Ciudad de México',
          state: 'CDMX',
          zipCode: '06700',
          country: 'México'
        },
        items: [
          {
            name: `Producto ${i + 1}`,
            quantity: 1 + i,
            weight: 0.5 + i * 0.2,
            value: 100 + i * 50
          }
        ],
        total: 100 + i * 50,
        status: 'pending',
        createdAt: new Date(Date.now() - i * 3600000).toISOString(),
        syncedAt: new Date().toISOString()
      }));
      
      setOrders(prev => [...prev, ...mockOrders]);
      setSyncStatus(prev => ({ ...prev, [integrationId]: 'success' }));
      
      // Update integration orders count
      setIntegrations(prev => prev.map(integration => 
        integration.id === integrationId 
          ? { ...integration, ordersCount: integration.ordersCount + mockOrders.length }
          : integration
      ));
      
      return { success: true, orders: mockOrders };
    } catch (error) {
      setSyncStatus(prev => ({ ...prev, [integrationId]: 'error' }));
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const createShipmentFromOrder = async (orderId) => {
    try {
      setLoading(true);
      setError(null);
      
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Orden no encontrada');
      }
      
      // Convert order to shipment data
      const shippingData = {
        origin: {
          address: 'Av. Insurgentes Sur 1602',
          city: 'Ciudad de México',
          state: 'CDMX',
          zipCode: '03100'
        },
        destination: {
          address: order.shippingAddress.address,
          city: order.shippingAddress.city,
          state: order.shippingAddress.state,
          zipCode: order.shippingAddress.zipCode
        },
        weight: order.items.reduce((sum, item) => sum + (item.weight * item.quantity), 0),
        dimensions: {
          length: 30,
          width: 25,
          height: 15
        },
        value: order.total,
        insurance: order.total > 500,
        urgency: 'standard'
      };
      
      // Create shipment
      const result = await createShipment('fedex_ground', shippingData);
      
      if (result.success) {
        // Update order status
        setOrders(prev => prev.map(o => 
          o.id === orderId 
            ? { ...o, status: 'shipped', shipmentId: result.shipment.id }
            : o
        ));
        
        return { success: true, shipment: result.shipment };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getOrdersByIntegration = (integrationId) => {
    return orders.filter(order => order.integrationId === integrationId);
  };

  const getPlatformConfig = (platformId) => {
    return availablePlatforms.find(p => p.id === platformId);
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    loading,
    error,
    integrations,
    orders,
    syncStatus,
    availablePlatforms,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    testConnection,
    syncOrders,
    createShipmentFromOrder,
    getOrdersByIntegration,
    getPlatformConfig,
    clearError,
    refreshIntegrations: loadUserIntegrations
  };

  return (
    <EcommerceContext.Provider value={value}>
      {children}
    </EcommerceContext.Provider>
  );
};

export default EcommerceContext;